/**
 * Unified AI Suggestion Service
 *
 * Single service for both manual (agent-triggered) and automatic (webhook-triggered)
 * AI suggestions. All suggestions are stored in MongoDB via the ui-backend.
 *
 * Endpoints:
 *   POST   /v1/gomake-ai-service/suggestion              — Create suggestion (async, returns 202)
 *   GET    /v1/gomake-ai-service/suggestion/:convId       — Get latest suggestion
 *   PATCH  /v1/gomake-ai-service/suggestion/:suggestionId — Record agent action
 */

import { getUserToken } from '@/services/storage-data';
import { createLogger } from '@/services/frontdesk/utils/logger';
import config from '@/config';
import type {
  AiAssistConversationHistory,
  ChatwootConversation,
  ChatwootMessage,
} from '@/services/frontdesk/api/chatwoot/types/conversations.types';
import { chatwootService } from '@/services/frontdesk/api/chatwoot/chatwoot.service';
import { stripEmailQuotes } from '@/widgets/frontdesk/providers/chatwoot/utils/conversation-utils';

const logger = createLogger('SuggestionService');

const BASE_URL = config.api_server || 'http://localhost:3000';

// ── Types ────────────────────────────────────────────────────────────────────

export interface SuggestionInput {
  emailBody: string;
  subject: string;
  customerId?: string;
  attachments?: Array<{ url: string; type: string }>;
  conversationHistory?: AiAssistConversationHistory;
}

/** Error `code` when the conversation has no customer message for AI Assist to answer. */
export const NO_CUSTOMER_MESSAGE = 'NO_CUSTOMER_MESSAGE';

export interface SuggestionOutput {
  suggestedResponse: string;
  confidence: number | null;
  modelUsed?: string;
  tokensUsed?: number;
  processingTimeMs?: number;
}

export interface Suggestion {
  id: string;
  conversationId: string;
  printHouseId: string;
  source: 'webhook' | 'manual';
  triggeredBy: string;
  input: SuggestionInput;
  output: SuggestionOutput | null;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  error: string | null;
  agentAction: 'pending' | 'accepted' | 'edited' | 'rejected' | null;
  agentEditedResponse: string | null;
  agentId: string | null;
  agentActionAt: string | null;
  requestedAt: string;
  completedAt: string | null;
  createdAt: string;
}

export type AgentAction = 'accepted' | 'edited' | 'rejected';

// ── AI Assist input ──────────────────────────────────────────────────────────

function isCustomerMessage(message: ChatwootMessage | null | undefined): message is ChatwootMessage {
  if (!message) return false;
  const type = message.message_type as unknown;
  const incoming = type === 0 || type === 'incoming' || type === '0';
  const isPrivate = message.private === true || message._private === true;
  // Conversation-list previews are synthetic; only real message rows carry direction
  const isPreview = String(message.id ?? '').startsWith('preview-');
  return incoming && !isPrivate && !isPreview;
}

function hasUsableContent(message: ChatwootMessage): boolean {
  const text =
    message.content_attributes?.email?.text_content?.full ||
    message.content_attributes?.email?.html_content?.full ||
    message.content ||
    '';
  return Boolean(text.trim()) || (Array.isArray(message.attachments) && message.attachments.length > 0);
}

/** Newest customer message among the given message rows. */
function findLatestCustomerMessage(messages: ChatwootMessage[]): ChatwootMessage | null {
  const candidates = messages.filter(
    (m): m is ChatwootMessage => isCustomerMessage(m) && hasUsableContent(m)
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((latest, m) =>
    (m.created_at ?? 0) > (latest.created_at ?? 0) ? m : latest
  );
}

/**
 * Hub resolves the latest customer message + bounded prior history from stored
 * message direction. If that endpoint is unavailable, fall back to the latest
 * page of real message rows (previews excluded) without history.
 */
type AiAssistAutomationIdentity = {
  hubMessageId?: string;
  providerMessageId?: string;
  sourceConversationId?: string;
  inboundMessageKey?: string;
  frontdeskAutomationRun?: Record<string, unknown>;
};

async function resolveAiAssistInput(conversation: ChatwootConversation): Promise<{
  latestInbound: ChatwootMessage | null;
  conversationHistory: AiAssistConversationHistory | null;
  /** True when the hub resolved the input from stored message direction */
  authoritative: boolean;
  automation: AiAssistAutomationIdentity;
}> {
  try {
    const input = await chatwootService.conversations.getAiAssistInput(conversation.id);
    const latestInbound = isCustomerMessage(input.latest_inbound) ? input.latest_inbound : null;
    const automation: AiAssistAutomationIdentity = {};
    if (typeof input.hub_message_id === 'string' && input.hub_message_id.trim()) {
      automation.hubMessageId = input.hub_message_id.trim();
    }
    if (typeof input.provider_message_id === 'string' && input.provider_message_id.trim()) {
      automation.providerMessageId = input.provider_message_id.trim();
    }
    if (typeof input.source_conversation_id === 'string' && input.source_conversation_id.trim()) {
      automation.sourceConversationId = input.source_conversation_id.trim();
    }
    if (typeof input.inbound_message_key === 'string' && input.inbound_message_key.trim()) {
      automation.inboundMessageKey = input.inbound_message_key.trim();
    }
    if (
      input.frontdesk_automation_run &&
      typeof input.frontdesk_automation_run === 'object' &&
      !Array.isArray(input.frontdesk_automation_run)
    ) {
      automation.frontdeskAutomationRun = input.frontdesk_automation_run;
    }
    return {
      latestInbound,
      conversationHistory: latestInbound ? input.conversation_history ?? null : null,
      authoritative: true,
      automation: latestInbound ? automation : {},
    };
  } catch (error) {
    logger.warn('AI Assist input unavailable from hub — using recent message rows', {
      conversationId: conversation.id,
      status: (error as { status?: number })?.status,
    });
    const rows: ChatwootMessage[] = [...(conversation.messages || [])];
    try {
      const page = await chatwootService.messages.listMessages(0, conversation.id);
      rows.push(...((page?.payload || []) as unknown as ChatwootMessage[]));
    } catch (listError) {
      logger.warn('Failed to load messages for AI Assist fallback', {
        conversationId: conversation.id,
        status: (listError as { status?: number })?.status,
      });
    }
    return {
      latestInbound: findLatestCustomerMessage(rows),
      conversationHistory: null,
      authoritative: false,
      automation: {},
    };
  }
}

// ── API Functions ────────────────────────────────────────────────────────────

/**
 * Request a new AI suggestion (manual trigger from agent).
 * Returns 202 Accepted — suggestion is processed async.
 * Returns 409 if a suggestion is already in progress.
 */
export async function requestSuggestion(
  conversation: ChatwootConversation,
  agentId?: string
): Promise<{ id: string; conversationId: string; status: string }> {
  const token = getUserToken();
  if (!token) throw new Error('No authentication token available');
  if (!conversation.id) throw new Error('Conversation ID is required');

  // AI Assist answers the customer: input is the latest customer message, never an
  // agent/AI outgoing message, activity row or private note.
  const { latestInbound, conversationHistory, authoritative, automation } =
    await resolveAiAssistInput(conversation);
  if (!latestInbound) {
    if (!authoritative) {
      throw new Error('AI Assist input could not be resolved');
    }
    throw Object.assign(new Error('No customer message to analyze'), {
      code: NO_CUSTOMER_MESSAGE,
    });
  }
  const messages = [latestInbound];

  const rawBody =
    latestInbound.content_attributes?.email?.text_content?.full ||
    latestInbound.content_attributes?.email?.html_content?.full ||
    latestInbound.content ||
    '';
  const htmlContent = latestInbound.content_attributes?.email?.html_content?.full;
  const emailBody = stripEmailQuotes(rawBody, htmlContent);

  // Native email: additional_attributes.mail_subject; Chatwoot: message email.subject
  const subjectFromConversation =
    conversation.additional_attributes?.mail_subject ||
    conversation.additional_attributes?.subject ||
    (conversation as { subject?: string }).subject ||
    conversation.custom_attributes?.subject;
  let subject =
    typeof subjectFromConversation === 'string' ? subjectFromConversation.trim() : '';
  if (!subject) {
    for (const msg of messages) {
      const emailSubject =
        msg?.content_attributes?.email?.subject ||
        msg?.content_attributes?.subject;
      if (typeof emailSubject === 'string' && emailSubject.trim()) {
        subject = emailSubject.trim();
        break;
      }
    }
  }

  const customerId =
    (conversation?.meta?.sender?.custom_attributes?.gomake_customer_id as string | undefined) ||
    (conversation?.custom_attributes?.gomake_customer_id as string | undefined);

  // Collect customer attachments only (agent/outgoing files are not customer input)
  const attachments: Array<{ url: string; type: string }> = [];
  const seenAttachmentUrls = new Set<string>();
  for (const msg of [...(conversation.messages || []), latestInbound]) {
    if (!isCustomerMessage(msg) || !Array.isArray(msg.attachments)) continue;
    for (const att of msg.attachments) {
      // Relative URLs are authenticated hub endpoints the AI service cannot fetch.
      if (att.data_url && !att.data_url.startsWith('/') && !seenAttachmentUrls.has(att.data_url)) {
        seenAttachmentUrls.add(att.data_url);
        attachments.push({ url: att.data_url, type: att.file_type || 'file' });
      }
    }
  }

  const url = `${BASE_URL}/v1/gomake-ai-service/suggestion`;

  logger.info('Requesting AI suggestion (manual)', {
    conversationId: conversation.id,
    hasCustomerId: !!customerId,
    hasSubject: !!subject,
    attachmentsCount: attachments.length,
    historyMessages: conversationHistory?.included_message_count ?? 0,
    hasAutomationRun: !!automation.frontdeskAutomationRun,
    inboundMessageKey: automation.inboundMessageKey || null,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      conversationId: conversation.id.toString(),
      source: 'manual',
      triggeredBy: agentId || 'manual',
      inboxId:
        conversation.inbox_id != null
          ? String(conversation.inbox_id)
          : undefined,
      ...(automation.hubMessageId ? { hubMessageId: automation.hubMessageId } : {}),
      ...(automation.providerMessageId
        ? { providerMessageId: automation.providerMessageId }
        : {}),
      ...(automation.sourceConversationId
        ? { sourceConversationId: automation.sourceConversationId }
        : {}),
      ...(automation.inboundMessageKey
        ? { inboundMessageKey: automation.inboundMessageKey }
        : {}),
      ...(automation.frontdeskAutomationRun
        ? { frontdeskAutomationRun: automation.frontdeskAutomationRun }
        : {}),
      input: {
        emailBody,
        subject,
        customerId,
        ...(attachments.length > 0 && { attachments }),
        ...(conversationHistory && { conversationHistory }),
      },
    }),
  });

  if (response.status === 409) {
    throw Object.assign(new Error('AI suggestion already in progress'), { statusCode: 409 });
  }

  if (response.status === 402) {
    const errorData = await response.json().catch(() => null);
    throw Object.assign(
      new Error(
        errorData?.message ||
          errorData?.error ||
          'Your AI Credits have been used. Contact us to load more credits.'
      ),
      { statusCode: 402, contactUs: true, creditGate: errorData?.creditGate }
    );
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `Request failed with status ${response.status}`);
  }

  const data = await response.json();
  return data?.data || data;
}

/**
 * Get the latest completed suggestion for a conversation.
 * Returns null if no suggestion exists, or if the agent already
 * accepted/edited/rejected it (so it is not re-injected into the composer).
 */
export async function getLatestSuggestion(
  conversationId: string | number
): Promise<{ suggestion: string; suggestionId: string; confidence: number | null; source: string; agentAction?: string } | null> {
  const token = getUserToken();
  if (!token) {
    logger.warn('No authentication token - cannot fetch suggestion');
    return null;
  }

  const url = `${BASE_URL}/v1/gomake-ai-service/suggestion/${conversationId}`;
  logger.info('Fetching latest suggestion', { conversationId });

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      logger.debug('No suggestion found', { conversationId, status: response.status });
      return null;
    }

    const data = await response.json();
    const record = data?.data || data;

    // Already used — do not put back into the reply box
    const agentAction = record?.agentAction || 'pending';
    if (agentAction === 'accepted' || agentAction === 'edited' || agentAction === 'rejected') {
      logger.info('Suggestion already handled by agent — skipping composer inject', {
        conversationId,
        agentAction,
      });
      return null;
    }

    // Handle both structured (new) and flat (old) response shapes
    let suggestionText = record?.output?.suggestedResponse || record?.suggestedResponse || record?.suggestion;
    const suggestionId = record?.id || record?._id || '';
    const confidence = record?.output?.confidence ?? record?.confidence ?? null;
    const source = record?.source || 'unknown';

    // suggestedResponse may be a JSON string
    if (typeof suggestionText === 'string') {
      try {
        const parsed = JSON.parse(suggestionText);
        if (parsed?.message) {
          suggestionText = parsed.message;
        }
      } catch (_e) {
        // Not JSON — use as-is
      }
    }

    if (!suggestionText) return null;

    logger.info('Suggestion found', { conversationId, source, hasConfidence: confidence !== null });

    return { suggestion: suggestionText, suggestionId, confidence, source, agentAction };
  } catch (error) {
    logger.warn('Failed to fetch suggestion (non-critical)', error);
    return null;
  }
}

/**
 * Record what the agent did with a suggestion (for training data).
 */
export async function recordAgentAction(
  suggestionId: string,
  action: AgentAction,
  agentId?: string,
  editedResponse?: string
): Promise<void> {
  const token = getUserToken();
  if (!token || !suggestionId) return;

  const url = `${BASE_URL}/v1/gomake-ai-service/suggestion/${suggestionId}`;

  logger.info('Recording agent action', { suggestionId, action });

  try {
    await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        agentAction: action,
        ...(editedResponse && { agentEditedResponse: editedResponse }),
        ...(agentId && { agentId }),
      }),
    });
  } catch (error) {
    // Non-critical — don't block the agent
    logger.warn('Failed to record agent action', error);
  }
}

export interface AISuggestionListItem {
  id: string;
  conversationId: string;
  /** Chatwoot/native inbox public id — used for inbox ACL filtering */
  inboxId?: string | null;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  source?: string;
  subject?: string | null;
  senderEmail?: string | null;
  suggestionPreview?: string | null;
  hasSuggestion: boolean;
  createdAt?: string;
  completedAt?: string | null;
  requestedAt?: string;
}

/**
 * List AI suggestions for the current print house (FrontDesk notifications tab).
 */
export async function listAISuggestions(options?: {
  status?: 'completed' | 'processing' | 'queued' | 'failed';
  limit?: number;
}): Promise<{
  items: AISuggestionListItem[];
  counts: { processing: number; completed: number; total: number };
} | null> {
  const token = getUserToken();
  if (!token) return null;

  const params = new URLSearchParams();
  params.set('status', options?.status || 'completed');
  params.set('limit', String(options?.limit || 30));

  const url = `${BASE_URL}/v1/gomake-ai-service/suggestions?${params.toString()}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) return null;
    const data = await response.json();
    const payload = data?.data || data;
    return {
      items: Array.isArray(payload?.items) ? payload.items : [],
      counts: payload?.counts || { processing: 0, completed: 0, total: 0 },
    };
  } catch (error) {
    logger.warn('Failed to list AI suggestions', error);
    return null;
  }
}

/**
 * Inbox public IDs the current user owns or is assigned to
 * (`can_access_conversations` / `is_owner`).
 * Strict: missing ACL flags do not grant access (unlike conversation filters).
 * Cached briefly — notification poll must not hit list-inboxes every cycle.
 */
let accessibleInboxIdsCache: {
  ids: Set<string>;
  expiresAt: number;
} | null = null;
const ACCESSIBLE_INBOX_CACHE_MS = 2 * 60 * 1000;

export async function getAccessibleInboxIdsForCurrentUser(): Promise<
  Set<string>
> {
  if (
    accessibleInboxIdsCache &&
    Date.now() < accessibleInboxIdsCache.expiresAt
  ) {
    return accessibleInboxIdsCache.ids;
  }

  const { chatwootService } = await import('@/services/frontdesk');
  const inboxesResponse = await chatwootService.inboxes.listAllInboxes();
  const inboxesArray =
    inboxesResponse.payload || inboxesResponse.data?.payload || [];
  const ids = new Set(
    inboxesArray
      .filter(
        (inbox: {
          can_access_conversations?: boolean;
          is_owner?: boolean;
        }) =>
          inbox.is_owner === true || inbox.can_access_conversations === true
      )
      .map((inbox: { id?: number | string }) => String(inbox.id))
  );
  accessibleInboxIdsCache = {
    ids,
    expiresAt: Date.now() + ACCESSIBLE_INBOX_CACHE_MS,
  };
  return ids;
}

/**
 * List AI suggestions scoped to inboxes the current user owns or is assigned to.
 */
export async function listAccessibleAISuggestions(options?: {
  status?: 'completed' | 'processing' | 'queued' | 'failed';
  limit?: number;
}): Promise<{
  items: AISuggestionListItem[];
  counts: { processing: number; completed: number; total: number };
} | null> {
  const data = await listAISuggestions(options);
  if (!data) return null;

  try {
    const accessibleInboxIds = await getAccessibleInboxIdsForCurrentUser();

    const withInbox = data.items.filter(
      (item) =>
        item.inboxId != null && accessibleInboxIds.has(String(item.inboxId))
    );
    const missingInbox = data.items.filter((item) => item.inboxId == null);

    let legacyAllowed = new Set<string>();
    if (missingInbox.length > 0) {
      const token = getUserToken();
      const hub = config.messaging_server || 'http://localhost:8080';
      if (token) {
        const res = await fetch(
          `${hub.replace(/\/$/, '')}/frontdesk/conversations/filter-accessible`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              ids: missingInbox.map((i) => i.conversationId),
            }),
          }
        );
        if (res.ok) {
          const body = await res.json();
          const payload = body?.data || body;
          const ids = Array.isArray(payload?.accessibleIds)
            ? payload.accessibleIds
            : [];
          legacyAllowed = new Set(ids.map(String));
        }
      }
    }

    const items = [
      ...withInbox,
      ...missingInbox.filter((item) =>
        legacyAllowed.has(String(item.conversationId))
      ),
    ];

    return {
      items,
      counts: {
        processing: items.filter(
          (i) => i.status === 'processing' || i.status === 'queued'
        ).length,
        completed: items.filter((i) => i.status === 'completed').length,
        total: items.length,
      },
    };
  } catch (error) {
    logger.warn('Failed to apply inbox ACL to AI suggestions', error);
    return { items: [], counts: { processing: 0, completed: 0, total: 0 } };
  }
}
